import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Shield, Lock } from "lucide-react";

/**
 * StaffLoginPage — dark professional login screen for the staff/sales portal.
 * Uses the standard `useAuth().login` (same backend endpoint as customers)
 * but after auth checks the role and only lets owner/sales continue into
 * /staff. Regular users are redirected back to the customer app with a
 * friendly toast.
 */
export default function StaffLoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (!u || !["owner", "admin", "sales"].includes(u.role)) {
        toast.error("This account doesn't have staff access. Contact your admin.");
        setBusy(false);
        return;
      }
      toast.success(`Welcome back, ${u.name?.split(" ")[0] || "team"}`);
      nav("/staff", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0B0F17] text-gray-100 flex items-center justify-center px-6"
      data-testid="staff-login-page"
      style={{
        backgroundImage:
          "radial-gradient(circle at 10% 20%, rgba(122,30,42,0.25) 0px, transparent 40%), radial-gradient(circle at 90% 80%, rgba(201,162,74,0.15) 0px, transparent 40%)",
      }}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#C9A24A] to-[#7A1E2A] mx-auto flex items-center justify-center mb-4 shadow-2xl shadow-[#7A1E2A]/50">
            <Shield size={22} className="text-white" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A24A] font-bold mb-2">Staff portal</p>
          <h1 className="text-3xl font-display text-white mb-1">Sign in to continue</h1>
          <p className="text-sm text-gray-400">
            For team members only. Customer sign-in →{" "}
            <a href="/login" className="text-[#C9A24A] hover:underline">go here</a>.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-[#0F1420] border border-white/10 rounded-2xl p-6 shadow-2xl space-y-4"
        >
          <div>
            <label className="text-xs font-semibold text-gray-300 mb-1.5 block uppercase tracking-widest">Work email</label>
            <Input
              data-testid="staff-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11 bg-[#0B0F17] border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-[#C9A24A]"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-300 mb-1.5 block uppercase tracking-widest">Password</label>
            <Input
              data-testid="staff-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-11 bg-[#0B0F17] border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-[#C9A24A]"
            />
          </div>
          <Button
            data-testid="staff-login-submit"
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-lg bg-gradient-to-r from-[#C9A24A] to-[#A8842E] hover:brightness-110 text-[#1B0F14] font-bold uppercase tracking-widest text-xs shadow-lg"
          >
            {busy ? "Signing in…" : (<><Lock size={13} className="mr-1.5" /> Sign in</>)}
          </Button>
          <p className="text-[11px] text-gray-500 text-center pt-2">
            Forgot your credentials? Ask your admin to reset via Team Members.
          </p>
        </form>
      </div>
    </div>
  );
}
